// WebSocketStatusIndicator.tsx
import React, {useEffect, useRef, useState} from "react";
import websocketAddress from "../_sig_api/_websocketAdress";
import {websocketSubscriber} from "../_helper/websocketBehave";
import {isEmpty} from "../_helper/isEmpty";
import {OnOpenMessage} from "../_messages/OnOpenMessage";
import {msgTypeOnOpen, msgTypeIni} from "../_messages/MessageTypes";
import {IlsUser} from "../_interfaces/IlsUser";
import {s_char} from "../_signals/s_char";
import {s_ini} from "../_signals/s_ini";
import {IniMessage} from "../_messages/IniMessage";
import wsUpdates from "./wsUpdates";
import {IniAddMessage} from "../_messages/IniAddMessage";
import {IniClearMessage} from "../_messages/IniClearMessage";
import {IniSetWaitMessage} from "../_messages/IniSetWaitMessage";
import {IniSetCurrentMessage} from "../_messages/IniSetCurrentMessage";
import {IniSetNextMessage} from "../_messages/IniSetNextMessage";
import {IniUnSetCurrentMessage} from "../_messages/IniUnSetCurrentMessage";
import {ResultsAddMessage} from "../_messages/ResultsAddMessage";
import {ResultsClearMessage} from "../_messages/ResultsClearMessage";
import {CharacterFieldsUpdateMessage} from "../_messages/CharacterFieldsUpdateMessage";
import {CharacterModsAddMessage} from "../_messages/CharacterModsAddMessage";
import {CharacterModsUpdateMessage} from "../_messages/CharacterModsUpdateMessage";
import {s_app_rid, s_app_sl} from "../_signals/s_app";
import {CharacterAddMessage} from "../_messages/CharacterAddMessage";
import {CharacterDeleteMessage} from "../_messages/CharacterDeleteMessage";

interface MsgBase {
    type: string;
    payload: object;
}

export type WebSocketMessage =
    MsgBase
    | OnOpenMessage
    | IniMessage
    | IniAddMessage
    | IniClearMessage
    | IniSetWaitMessage
    | IniSetCurrentMessage
    | IniSetNextMessage
    | IniUnSetCurrentMessage
    | ResultsAddMessage
    | ResultsClearMessage
    | CharacterFieldsUpdateMessage
    | CharacterModsAddMessage
    | CharacterModsUpdateMessage
    | CharacterAddMessage
    | CharacterDeleteMessage;

interface WebSocketServiceProps {
    onNewRound?: (isNewRound: boolean) => void;
    onHasIni?: (hasIni: boolean) => void;
}

const WebSocketService: React.FC<WebSocketServiceProps> = ({onNewRound, onHasIni}) => {
    const [connected, setConnected] = useState(false);
    const websocket = useRef<WebSocket | null>(null);
    const reconnectTimeout = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        connectWebsocket();

        websocketSubscriber.subscribe((msg) => {
            if (!isEmpty(msg)) {
                if (websocket.current && websocket.current.readyState === WebSocket.OPEN) {
                    websocket.current.send(JSON.stringify(msg));
                } else {
                    console.warn('Nachricht konnte nicht gesendet werden, WebSocket nicht offen:', msg);
                }
            }
        });

        return () => {
            disconnectWebsocket();
        };
    }, []);

    const disconnectWebsocket = () => {
        console.log("wsDisconnect");
        if (websocket.current) {
            websocket.current.close();
        }
    };

    const reconnectWebsocket = () => {
        reconnectTimeout.current = setTimeout(() => {
            if (!websocket.current || websocket.current.readyState === WebSocket.CLOSED) {
                connectWebsocket();
            }
        }, 3000);
    };

    const connectWebsocket = () => {
        console.log('wsConnect');

        if (websocket.current) {
            websocket.current.close();
        }

        websocket.current = new WebSocket(websocketAddress);

        const user = localStorage.getItem('user');
        const char = localStorage.getItem('char');
        const round = localStorage.getItem('round');

        websocket.current.onopen = () => {
            console.log('websocket.onopen - Verbindung hergestellt');
            setConnected(true);

            if (user && char && round) {
                const userData: IlsUser = JSON.parse(user);
                const onopen: OnOpenMessage = {
                    type: msgTypeOnOpen,
                    uid: userData.id,
                    cid: s_char.value.id,
                    rid: round,
                    isSL: s_app_sl.value
                };
                if (websocket.current) websocket.current.send(JSON.stringify(onopen));
            }
        };

        websocket.current.onclose = () => {
            console.log('websocket.onclose - Verbindung geschlossen');
            setConnected(false);
            reconnectWebsocket();
        };

        websocket.current.onerror = (event) => {
            console.error('websocket.onerror - WebSocket-Fehler:', event);
            setConnected(false);
            if (websocket.current && websocket.current.readyState === WebSocket.CLOSED) {
                reconnectWebsocket();
            }
        };

        websocket.current.onmessage = (event: MessageEvent) => {

            let data: WebSocketMessage = {type: '', payload: {}};
            try {
                if (typeof event.data === 'string' && event.data.length > 0) {
                    data = JSON.parse(event.data) as WebSocketMessage;
                    if (!data.hasOwnProperty('type')) data.type = 'none';
                } else {
                    console.log('Received non-string or empty WebSocket message (likely native frame).');
                    return;
                }
            } catch (e) {
                console.error('Fehler beim Parsen der WebSocket-Nachricht:', e);
                return;
            }

            console.log('Empfangene WebSocket-Nachricht:', data);
            if (data.hasOwnProperty('type') && data.type === msgTypeIni) {
                const msg = (data as IniMessage);
                if (msg.payload.rid == s_app_rid.value) {
                    s_ini.value = msg.payload.ini;
                    if (onNewRound) onNewRound(msg.payload.newRound);
                    const me = msg.payload.ini.findIndex(ini => ini.current && ini.cid === s_char.value.id);
                    if (onHasIni) onHasIni(me >= 0);
                }
            } else {
                wsUpdates(data);
            }
        };
    };

    return (
        <div data-tooltip = {connected ? 'websocket connected' : 'websocket disconnected'}
            style={{
            backgroundColor: connected ? 'green' : 'red',
            padding: 0,
            bottom: '-15px',
            left: '-15px',
            zIndex: 10000,
            rotate: '45deg',
            height: '30px',
            width: '30px',
            position: 'fixed'
        }}>&nbsp;</div>
    );
};

export default WebSocketService;